import { FormEvent, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_MY_BOSS_FEEDBACK_POSTS,
  CREATE_BOSS_FEEDBACK_POST,
} from "../../../api/boss-feedback";
import { notify, notifyError } from "../../../utils/toast";
import { getErrorMessage } from "../../../utils/error";
import BossBottomTab from "../components/boss-bottom-tab";

type BossFeedbackPost = {
  id: string;
  content: string;
  authorName: string;
  authorId: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  replyCount: number;
  replies: Array<{
    id: string;
    content: string;
    isAdmin: boolean;
    createdAt: string;
  }>;
};

const BossFeedback: React.FC = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState("");

  const { data, loading, refetch } = useQuery(GET_MY_BOSS_FEEDBACK_POSTS);

  const [createPost, { loading: creating }] = useMutation(
    CREATE_BOSS_FEEDBACK_POST,
    {
      onCompleted: () => {
        setContent("");
        refetch();
        notify("의견이 등록되었습니다!");
      },
      onError: (error) => {
        console.error("의견 제출 실패:", error);
        notifyError(`제출 실패: ${getErrorMessage(error)}`);
      },
    },
  );

  const posts: BossFeedbackPost[] = useMemo(
    () => data?.myBossFeedbackPosts || [],
    [data?.myBossFeedbackPosts],
  );
  const empty = useMemo(() => !loading && posts.length === 0, [loading, posts]);
  const canSubmit = useMemo(
    () => content.trim().length > 0 && !creating,
    [content, creating],
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    createPost({
      variables: {
        input: {
          content: content.trim(),
        },
      },
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-[120px]">
      <div className="px-4 py-5 pt-16">
        <button className="text-sm mb-3" onClick={() => navigate("/boss/home")}>
          ← 뒤로
        </button>
        <h1 className="text-lg font-semibold mb-4">의견/건의사항</h1>

        <div className="mb-4 p-3 bg-[#F4F5F7] rounded-lg text-sm text-gray-600 space-y-1">
          <p>
            불편한 점, 버그 신고, 추가되었으면 하는 기능 등 의견을 남겨주세요.
          </p>
          <p>의견 하나하나 모두 꼼꼼히 읽어보겠습니다.</p>
          <p className="font-semibold text-[#1562fc]">
            내가 제출한 의견만 볼 수 있습니다.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="mb-6 border-b border-gray-200 pb-6"
        >
          <div className="border border-gray-200 rounded-md p-3 bg-white">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="의견을 입력하세요"
              rows={4}
              className="w-full border-0 px-0 py-0 text-sm resize-none focus:outline-none"
              disabled={creating}
            />
            <div className="flex justify-end mt-2">
              <button
                type="submit"
                disabled={!canSubmit}
                className={`px-3 py-2 rounded-md text-sm ${
                  canSubmit
                    ? "bg-[#1562fc] text-white hover:bg-[#124ab7]"
                    : "bg-[#F4F5F7] text-[#BBBDC0]"
                }`}
              >
                {creating ? "등록 중..." : "등록"}
              </button>
            </div>
          </div>
        </form>

        <h2 className="text-sm font-medium mb-2">내가 제출한 의견</h2>
        {loading ? (
          <div className="text-sm text-gray-500">로딩 중...</div>
        ) : empty ? (
          <div className="text-sm text-gray-500">
            아직 제출한 의견이 없습니다.
          </div>
        ) : (
          <ul className="space-y-3">
            {posts.map((post) => {
              const hasReply = post.replyCount > 0;
              const hasAdminReply =
                post.replies?.some((r) => r.isAdmin) || false;
              const status = post.status?.toUpperCase();
              const isAnswered = status === "ANSWERED";
              return (
                <li key={post.id}>
                  <button
                    type="button"
                    className={`w-full text-left border rounded-lg p-3 transition-colors ${
                      hasReply
                        ? isAnswered || hasAdminReply
                          ? "bg-[#F4F5F7] border-[#1562fc]"
                          : "bg-[#F4F5F7] border-[#F4F5F7]"
                        : "bg-white border-[#F4F5F7]"
                    }`}
                    onClick={() => navigate(`/boss/feedback/detail/${post.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500">
                        {new Date(post.createdAt).toLocaleString()}
                      </div>
                      {hasReply && (
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            isAnswered || hasAdminReply
                              ? "bg-[#1562fc] text-white"
                              : "bg-[#F4F5F7] text-[#1562fc]"
                          }`}
                        >
                          {isAnswered || hasAdminReply
                            ? "✓ 운영자 답변"
                            : `${post.replyCount}개 답변`}
                        </span>
                      )}
                    </div>
                    {post.content && (
                      <p className="text-sm text-gray-700 mt-2 line-clamp-2">
                        {post.content}
                      </p>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <BossBottomTab />
    </div>
  );
};

export default BossFeedback;
